<template>
  <v-form
    ref="form"
    v-model="validForm"
    lazy-validation
  >
    <v-card
      max-width="700px"
      class="mx-auto"
      outlined
    >
      <v-card-title :class="$vuetify.breakpoint.mdAndDown ? 'pa-6' : 'pa-10'">
        <v-row class="justify-space-between align-center">
          <v-col>
            <h1 class="headline font-weight-medium">
              <span>{{ isEditing ? `Edit` : `New` }}</span>
              <span
                class="primary--text"
              >&nbsp;{{ capitalize(editedDiscussion.state) }}&nbsp;</span>
              <span>Discussion</span>
            </h1>
          </v-col>
          <v-col class="flex-grow-0 pa-0">
            <v-btn
              v-if="!isEditing"
              @click="closeModal"
              text
              fab
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-container :class="$vuetify.breakpoint.mdAndDown ? 'pa-0' : ''">
          <v-container class="grey--text">
            <span v-if="editedDiscussion.createdAt" class="mr-6">
              <strong>Created:</strong> {{ formatTimestamp(editedDiscussion.createdAt) }}
            </span>
            <span v-if="editedDiscussion.updatedAt">
              <strong>Updated:</strong> {{ formatTimestamp(editedDiscussion.updatedAt) }}
            </span>
            <p v-if="editedDiscussion.author && user.isAdmin && isEditing" class="mt-2">
              <strong>Author:</strong> {{ editedDiscussion.author.displayName }} (<a
                :href="'mailto:' + editedDiscussion.author.email"
              >{{ editedDiscussion.author.email }}</a>)
            </p>
          </v-container>

          <StatePickerMultiple
            @updateStates="updateStates"
            :selectedStates="editedDiscussion.states"
            :includeNational="true"
          />

          <v-col cols="12">
            <v-text-field
              v-model="editedDiscussion.headline"
              :disabed="!userCanEdit"
              :rules="[rules.required]"
              label="Headline"
              prepend-icon="mdi-format-title"
              class="headline"
              counter="60"
              maxlength="60"
            />

            <v-textarea
              v-model="editedDiscussion.description"
              :rules="[rules.required]"
              label="Description"
              prepend-icon="mdi-subtitles-outline"
              auto-grow
            />
            <v-text-field
              v-model="editedDiscussion.hyperlinkPhase"
              :disabed="!userCanEdit"
              label="Hyperlink Phrase"
              prepend-icon="mdi-format-close-quote"
            />

            <!-- <p>
              Description
            </p>

            <div>
              <vue-editor v-model="editedDiscussion.description" :editorToolbar="customToolbar" />
            </div> -->

            <!-- <ImageUpload
              @setImgFields="setImgFields"
              @setImgUrl="setImgUrl"
              :disabed="!userCanEdit"
              :editedItem="editedDiscussion"
              :imgUrl="editedDiscussion.imgUrl"
              label="Discussion Image"
            /> -->

            <!-- <p class="headline red--text">
              To upload multiple images into a carousel, use the following field.
            </p> -->

            <ImageUploadMultiple
              v-show="!isImagesEditable"
              @setImages="setImages"
              @removeImage="removeImage"
              :disabed="!userCanEdit"
              :editedItem="editedDiscussion"
              :existingImages="existingImages"
              label="Discussion Images"
            />

            <v-text-field
              v-show="
                !editedDiscussion.images ||
                  (editedDiscussion.images && editedDiscussion.images.length === 0)
              "
              v-model="editedDiscussion.videoLink"
              :disabed="!userCanEdit"
              label="Discussion Video Link"
              prepend-icon="mdi-link"
              clearable
            />

            <p class="red--text">
              Please do not enter Instagram links in the video link field.
            </p>

            <v-autocomplete
              v-model="tags"
              @change="updateTags"
              :items="discussionTags"
              :search-input.sync="tagSearch"
              cache-items
              item-text="label"
              item-value="key"
              prepend-icon="mdi-tag-outline"
              label="Tags"
              multiple
              chips
              deletable-chips
              dense
            />

            <v-select
              v-model="editedDiscussion.workflowState"
              :disabed="!userCanEdit"
              :items="Object.values(workflowStates)"
              :rules="[rules.required]"
              label="Discussion Status"
              prepend-icon="mdi-progress-check"
            />

            <RolesInputs
              @updateCategories="updateGroups"
              @updateRoles="updateRoles"
              @updateAffinityGroups="updateAffinityGroups"
              :filterLabel="'Filter discussion by'"
              :editingCategories="editedDiscussion.groups"
              :editingRoles="editedDiscussion.roles"
              :editingAffinityGroups="editedDiscussion.affinity_groups"
            />

            <v-row>
              <v-col>
                <v-switch
                  v-model="editedDiscussion.volunteerTypeStudent"
                  :disabled="!userCanEdit"
                  label="Show to student volunteers ONLY"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="editedDiscussion.volunteerTypeSurvivor"
                  :disabled="!userCanEdit"
                  label="Show to survivor volunteers ONLY"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-switch
                  v-model="editedDiscussion.isShowingToTesters"
                  :disabed="!userCanEdit"
                  label="Show only to Test group"
                />
              </v-col>
            </v-row>
          </v-col>

          <br><br>

          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="isEditing"
              @click="clearChanges"
              text
            >
              Clear Changes
            </v-btn>
            <v-btn
              v-if="!isEditing"
              @click="closeModal"
              color="primary darken-1"
              text
            >
              Cancel
            </v-btn>
            <v-btn
              @click="publish"
              color="primary"
              depressed
            >
              {{ isEditing ? 'Update' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { mapState } from 'vuex';
import BaseForm from './BaseForm';
import StatePickerMultiple from '../StatePickerMultiple';
import { RolesInputs } from 'components';
import ImageUploadMultiple from './ImageUploadMultiple.vue';

export default {
  name: 'DiscussionForm',

  components: {
    RolesInputs,
    StatePickerMultiple,
    //ImageUpload,
    ImageUploadMultiple,
  },

  extends: BaseForm,

  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
    editedDiscussion: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      workflowStates: {
        draft: {
          text: 'Draft',
          value: 'draft',
        },
        published: {
          text: 'Published',
          value: 'published',
        },
        archived: {
          text: 'Archived',
          value: 'archived',
        },
      },
      currentlySelectedStates: [],
      tags: [],
      tagSearch: null,
      existingImages: [],
      customToolbar: [['link']],
    };
  },

  computed: {
    ...mapState(['user', 'discussionTags']),

    schedulerValue: {
      get() {
        return this.editedDiscussion.isScheduled;
      },
      set(newValue) {
        this.editedDiscussion.isScheduled = newValue;
      },
    },
    isLinkEditable() {
      return this.editedDiscussion.images && this.editedDiscussion.images.length > 0;
    },
    isImagesEditable() {
      return this.editedDiscussion.videoLink && this.editedDiscussion.videoLink.length > 0;
    },
    userIsTester() {
      return this.user.isTester;
    },
    userCanEdit() {
      return !(!this.user.isAdmin && this.editedDiscussion.workflowState !== 'draft');
    },
  },

  watch: {
    tags() {
      this.tagSearch = '';
    },
  },

  async created() {
    this.existingImages = this.editedDiscussion.images
      ? this.editedDiscussion.images.map(imgData => JSON.parse(imgData))
      : [];

    await this.$store.dispatch('setDiscussionTags');
    this.tags = [...this.editedDiscussion.tags];
  },

  methods: {
    setImgFields(response) {
      this.editedDiscussion.imgPublicId = response.public_id;
      this.editedDiscussion.imgUrl = response.secure_url;
      this.editedDiscussion.imgFormat = response.format;
      this.editedDiscussion.imgWidth = response.width;
      this.editedDiscussion.imgHeight = response.height;
    },
    setImgUrl(urlToEdit) {
      this.editedDiscussion.imgUrl = urlToEdit;
    },
    setImages(imageArr) {
      this.editedDiscussion.images = imageArr;
      this.existingImages = imageArr;
    },
    removeImage(index) {
      this.editedDiscussion.images.splice(index, 1);
    },
    updateRoles(roles) {
      this.editedDiscussion.roles = roles;
    },

    updateGroups(groups) {
      this.editedDiscussion.groups = groups;
    },

    updateAffinityGroups(affinity_groups) {
      this.editedDiscussion.affinity_groups = affinity_groups;
    },

    updateStates(states) {
      this.editedDiscussion.states = states;
      this.currentlySelectedStates = states;
    },

    publish() {
      if (this.invalidForm) {
        return;
      }
      this.$emit(this.isEditing ? 'update-discussion' : 'create-discussion');
    },

    changeWorkflowState(state) {
      if (this.invalidForm) {
        return;
      }

      //Change the workflow state to the value passed
      this.editedDiscussion.workflowState = state;
      this.publish();
    },

    updateTags() {
      this.editedDiscussion.tags = [...this.tags];
    },
  },
};
</script>
